// Shared with navigation, route definitions and login preference resolution.
export const landingRoutes = {
  DASHBOARD: "/",
  MANAGEMENT_HUB: "/management",
  APPRENTICE_DIRECTORY: "/students",
  SEMESTER_REVIEWS: "/trainer/semester-reviews",
  INSTRUCTOR_TIMELINE: "/instructor-timeline",
  BONUS: "/bonus",
  VISIBILITY_SETTINGS: "/manage/visibility",
};

export const hasRole = (user, name) => (user?.roles || []).some((role) => {
  const raw = typeof role === "string" ? role : role?.name || role?.authority;
  return typeof raw === "string" && raw.toUpperCase().replace(/^ROLE_/, "") === name;
});

export const canChooseLandingPage = (user) => hasRole(user, "ADMIN") || hasRole(user, "BERUFSBILDNER");

export const getNavigationItems = (user, viewMode = "simple", canAccess = () => true) => {
  const isPracticeInstructor = () => hasRole(user, "PRACTICE_INSTRUCTOR") || hasRole(user, "PRAXISBILDNER");
  const isRevisor = () => hasRole(user, "REVISOR");
  const baseItems = [
    { to: landingRoutes.DASHBOARD, icon: "fa-tachometer-alt", label: "Dashboard" },
  ];

  if (canChooseLandingPage(user)) {
    const items = [
      ...baseItems,
      {
        to: landingRoutes.APPRENTICE_DIRECTORY,
        icon: "fa-user-graduate",
        label: "Apprentice Directory",
      },
      {
        to: landingRoutes.SEMESTER_REVIEWS,
        icon: "fa-clipboard-check",
        label: "Semester Reviews",
      },
    ];

    if (viewMode === "expert") {
      items.splice(1, 0, {
        to: landingRoutes.MANAGEMENT_HUB,
        icon: "fa-cogs",
        label: "Management Hub",
      });

      items.push(
        {
          to: landingRoutes.INSTRUCTOR_TIMELINE,
          icon: "fa-stream",
          label: "Instructor Timeline",
        },
        {
          to: "/admin/email-history",
          icon: "fa-envelope",
          label: "Email",
        },
        {
          to: landingRoutes.BONUS,
          icon: "fa-coins",
          label: "Bonus",
        },
        {
          to: "/invitations",
          icon: "fa-envelope-open-text",
          label: "Invitations",
        },
        {
          to: landingRoutes.VISIBILITY_SETTINGS,
          icon: "fa-eye",
          label: "Visibility Settings",
        },
      );

    }
    return items;
  } else if (isPracticeInstructor()) {
    return [
      {
        to: "/practice-instructor/students",
        icon: "fa-users",
        label: "My Students",
      },
      {
        to: landingRoutes.SEMESTER_REVIEWS,
        icon: "fa-clipboard-check",
        label: "My Reviews",
      },
    ];
  } else if (isRevisor()) {
    return [
      ...baseItems,
      {
        to: "/revisor/reviews",
        icon: "fas fa-shield-alt",
        label: "Review Control Center",
      },
    ];
  } else {
    const isItOrMedi =
      user?.professionType === "INFORMATIK" ||
      user?.professionType === "INFORMATIKER" ||
      user?.professionType === "MEDIAMATIK" ||
      user?.professionType === "MEDIAMATIKER";

    const items = [...baseItems];

    // Profession-based visibility gates (backend remains source of truth).
    if (canAccess("VIEW_GRADES")) {
      items.push({ to: "/grades", icon: "fa-graduation-cap", label: "Grades" });
    }

    if (isItOrMedi && canAccess("VIEW_SEMESTER_REVIEWS")) {
      items.push({
        to: "/learner/semester-reviews",
        icon: "fa-clipboard-check",
        label: "Semester Reviews",
      });
    }
    // KV / FKD / DHF learners do not see Semester Reviews in the nav

    if (canAccess("VIEW_LEARNING_DOCUMENTATION")) {
      items.push({
        to: "/learning-documentation",
        icon: "fa-book",
        label: "Learning Documentation",
      });
    }
    if (canAccess("VIEW_BONUS")) {
      items.push({
        to: "/my-bonus",
        icon: "fa-coins",
        label: "Bonus",
      });
    }
    items.push({
      to: "/videos",
      icon: "fa-video",
      label: "Videos",
    });

    return items;
  }
};


// Expert mode exposes all authorized destinations; simple mode is only a display preference.
export const getLandingPageOptions = (user) => {
  if (!canChooseLandingPage(user)) return [];
  const allowed = new Set(user?.availableLandingPages || []);
  return getNavigationItems(user, "expert").flatMap((item) => {
    const id = Object.keys(landingRoutes).find((key) => landingRoutes[key] === item.to);
    return id && allowed.has(id) ? [{ ...item, id }] : [];
  });
};

export const resolveDefaultLandingPage = (user) =>
  getLandingPageOptions(user).find((item) => item.id === user?.defaultLandingPage)?.to || landingRoutes.DASHBOARD;

// Only accept local protected locations passed by our router, never external URLs or auth routes.
export const safeReturnDestination = (from) => {
  if (!from || typeof from.pathname !== "string") return null;
  const path = from.pathname;
  if (!path.startsWith("/") || path.startsWith("//") || /[\\%\s]/.test(path) ||
      /^\/(login|register|logout|access|invite|onboarding|force-password-change)(\/|$)/.test(path)) return null;
  const search = typeof from.search === "string" && from.search.startsWith("?") ? from.search : "";
  const hash = typeof from.hash === "string" && from.hash.startsWith("#") ? from.hash : "";
  return path + search + hash;
};

export const resolvePostLoginDestination = (user, from) => {
  const explicit = safeReturnDestination(from);
  if (explicit) return explicit;
  if (canChooseLandingPage(user)) return resolveDefaultLandingPage(user);
  if (user?.defaultLandingPage) return landingRoutes.DASHBOARD; // Ignore a former manager's preference.
  if (hasRole(user, "PRACTICE_INSTRUCTOR") || hasRole(user, "PRAXISBILDNER")) return "/practice-instructor/students";
  return landingRoutes.DASHBOARD;
};
